import { useState } from 'react';
import { Download, ArrowUpDown } from 'lucide-react';

/**
 * Extract plain text from React children recursively.
 * ReactMarkdown passes nested elements (e.g. <strong>, <em>) as children.
 */
function extractText(children) {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (!children) return '';
  if (Array.isArray(children)) return children.map(extractText).join('');
  if (children.props?.children) return extractText(children.props.children);
  return '';
}

/**
 * Renders a markdown table with the same styling as QueryResultTable.
 * Used as a custom `table` component override for ReactMarkdown.
 *
 * Features: sortable columns, CSV export, currency/date formatting,
 * dark theme, horizontal scroll.
 */
export function MarkdownTable({ children }) {
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // Parse columns and rows from ReactMarkdown's table children
  let columns = [];
  let rows = [];

  try {
    const childArray = Array.isArray(children) ? children : [children];
    for (const child of childArray) {
      if (!child?.props?.children) continue;
      const tag = child.type || child.props?.node?.tagName;

      const trElements = Array.isArray(child.props.children)
        ? child.props.children
        : [child.props.children];

      for (const tr of trElements) {
        if (!tr?.props?.children) continue;
        const cells = Array.isArray(tr.props.children)
          ? tr.props.children
          : [tr.props.children];

        const cellTexts = cells
          .filter(c => c?.props?.children !== undefined)
          .map(c => extractText(c.props.children).trim());

        if (tag === 'thead' || (columns.length === 0 && cellTexts.length > 0)) {
          columns = cellTexts;
        } else if (cellTexts.length > 0) {
          rows.push(cellTexts);
        }
      }
    }
  } catch {
    // Fallback: render as plain table if parsing fails
    return (
      <table className="w-full text-sm border-collapse">
        {children}
      </table>
    );
  }

  if (columns.length === 0) {
    return (
      <table className="w-full text-sm border-collapse">
        {children}
      </table>
    );
  }

  // Sort
  const sortedRows = sortCol !== null
    ? [...rows].sort((a, b) => {
        const va = a[sortCol] ?? '';
        const vb = b[sortCol] ?? '';
        // Try numeric comparison (strip $, commas, %, etc.)
        const na = parseFloat(va.replace(/[$,%+]/g, '').replace(/,/g, ''));
        const nb = parseFloat(vb.replace(/[$,%+]/g, '').replace(/,/g, ''));
        const cmp = (!isNaN(na) && !isNaN(nb)) ? na - nb : va.localeCompare(vb);
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : rows;

  const toggleSort = (idx) => {
    if (sortCol === idx) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(idx); setSortDir('asc'); }
  };

  const exportCSV = () => {
    const header = columns.join(',');
    const body = sortedRows.map(r =>
      r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'table-export.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  // Detect if a column contains bold text (often totals row)
  const isBoldRow = (row) => row.some(cell => cell.startsWith('**') || cell.includes('Total'));

  return (
    <div className="border border-zinc-200 rounded-lg overflow-hidden my-3">
      <div className="flex justify-between items-center px-3 py-1.5 bg-zinc-50 text-xs text-zinc-500">
        <span>{rows.length} rows</span>
        <button onClick={exportCSV} className="flex items-center gap-1 hover:text-zinc-800 transition-colors">
          <Download className="h-3 w-3" /> CSV
        </button>
      </div>
      <div className="overflow-x-auto max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 sticky top-0">
            <tr>
              {columns.map((col, i) => (
                <th
                  key={i}
                  onClick={() => toggleSort(i)}
                  className="px-3 py-2 text-left text-zinc-700 cursor-pointer hover:text-zinc-900 whitespace-nowrap font-semibold border-b border-zinc-200"
                >
                  {col.replace(/\*\*/g, '')} <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-40" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, i) => {
              const bold = isBoldRow(row);
              return (
                <tr key={i} className={`border-t border-zinc-100 hover:bg-zinc-50 ${bold ? 'bg-zinc-50 font-semibold' : ''}`}>
                  {row.map((cell, j) => (
                    <td key={j} className="px-3 py-1.5 text-zinc-700 whitespace-nowrap">
                      {cell.replace(/\*\*/g, '')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
